import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Save, Loader2, CheckCircle, XCircle, Coins, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminTokenSettings() {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Hangi satırın o an güncellendiğini tutmak için (Loading efekti için)
    const [savingCode, setSavingCode] = useState(null);
    // Başarı mesajları için
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchFeatures();
    }, []);

    const fetchFeatures = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/admin/features`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFeatures(response.data);
            setError(null);
        } catch (err) {
            console.error("Özellikler çekilemedi:", err);
            setError("Veriler yüklenirken hata oluştu. Oturumunuzun süresi dolmuş olabilir.");
        } finally {
            setLoading(false);
        }
    };

    // İnput değiştiğinde sadece o satırın state'ini günceller
    const handleCostChange = (code, newValue) => {
        setFeatures(features.map(f => 
            f.feature_code === code ? { ...f, token_cost: parseInt(newValue) || 0 } : f
        ));
    };

    // Kaydet butonuna basıldığında Backend'e gönderir
    const handleSave = async (feature_code, token_cost) => {
        setSavingCode(feature_code);
        setSuccessMessage('');
        setError(null);

        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_URL}/api/admin/features/${feature_code}`,
                { token_cost: token_cost },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setSuccessMessage(`'${feature_code}' başarıyla ${token_cost} Token olarak güncellendi!`);
            
            // 3 saniye sonra başarı mesajını gizle
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error("Güncelleme hatası:", err);
            setError(`${feature_code} güncellenemedi.`);
        } finally {
            setSavingCode(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <Loader2 className="animate-spin text-blue-500 w-12 h-12" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-gray-200 p-6 md:p-10 font-sans">
            <div className="max-w-4xl mx-auto">
                
                {/* Üst Başlık */}
                <div className="flex items-center gap-4 mb-8 border-b border-gray-800 pb-6">
                    <div className="p-3 bg-blue-900/30 rounded-xl border border-blue-500/30">
                        <Settings className="w-8 h-8 text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-extrabold text-white">Token Maliyet Yönetimi</h1>
                        <p className="text-gray-400 mt-1">Uygulama özelliklerinin token ücretlerini veya ödüllerini buradan anlık olarak değiştirebilirsiniz.</p>
                    </div>
                </div>

                {/* Bildirim Alanı */}
                {error && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 bg-red-900/40 border border-red-700 rounded-xl flex items-center gap-3 text-red-400">
                        <XCircle className="w-5 h-5 flex-shrink-0" />
                        <p>{error}</p>
                    </motion.div>
                )}
                {successMessage && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 bg-green-900/40 border border-green-700 rounded-xl flex items-center gap-3 text-green-400">
                        <CheckCircle className="w-5 h-5 flex-shrink-0" />
                        <p>{successMessage}</p>
                    </motion.div>
                )}

                {/* Tablo/Liste Görünümü */}
                <div className="bg-gray-800 rounded-2xl border border-gray-700 shadow-xl overflow-hidden">
                    {/* Tablo Başlıkları (Mobilde gizli, masaüstünde görünür) */}
                    <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-gray-900/50 border-b border-gray-700 font-semibold text-sm text-gray-400 uppercase tracking-wider">
                        <div className="col-span-5 pl-2">Özellik Adı</div>
                        <div className="col-span-3">Sistem Kodu</div>
                        <div className="col-span-4 text-right pr-2">Token Miktarı</div>
                    </div>

                    {/* Liste Elemanları */}
                    <div className="divide-y divide-gray-700/50">
                        {features.map((feature) => (
                            <motion.div 
                                key={feature.feature_code}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 items-center hover:bg-gray-700/30 transition-colors"
                            >
                                {/* İsim */}
                                <div className="col-span-1 md:col-span-5">
                                    <p className="font-bold text-white text-lg md:text-base">{feature.name}</p>
                                    {feature.feature_code.includes('reward') && (
                                        <span className="inline-block mt-1 px-2 py-0.5 bg-yellow-900/30 text-yellow-500 border border-yellow-700/50 rounded text-xs font-bold">ÖDÜL</span>
                                    )}
                                </div>
                                
                                {/* Kod */}
                                <div className="col-span-1 md:col-span-3">
                                    <code className="px-2 py-1 bg-gray-900 rounded text-xs text-blue-300 font-mono border border-gray-700">
                                        {feature.feature_code}
                                    </code>
                                </div>

                                {/* Input ve Kaydet Butonu */}
                                <div className="col-span-1 md:col-span-4 flex items-center justify-end gap-3 mt-2 md:mt-0">
                                    <div className="relative w-28">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Coins className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            min="0"
                                            value={feature.token_cost}
                                            onChange={(e) => handleCostChange(feature.feature_code, e.target.value)}
                                            className="block w-full pl-9 pr-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                        />
                                    </div>
                                    <button
                                        onClick={() => handleSave(feature.feature_code, feature.token_cost)}
                                        disabled={savingCode === feature.feature_code}
                                        className={`flex items-center justify-center w-10 h-10 md:w-auto md:px-4 rounded-lg font-bold transition-all
                                        ${savingCode === feature.feature_code 
                                            ? 'bg-blue-600/50 cursor-not-allowed' 
                                            : 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/30 active:scale-95'}`}
                                        title="Kaydet"
                                    >
                                        {savingCode === feature.feature_code ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <>
                                                <Save className="w-5 h-5 md:mr-2" />
                                                <span className="hidden md:inline">Kaydet</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </motion.div>
                        ))}

                        {features.length === 0 && !loading && (
                            <div className="p-8 text-center text-gray-400 flex flex-col items-center">
                                <AlertCircle className="w-10 h-10 mb-3 opacity-50" />
                                <p>Sistemde henüz kayıtlı bir özellik (app_features) bulunmuyor.</p>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
}